import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { User } from "~/api/model";
import {
  getGetCurrentUserQueryKey,
  useGetCurrentUser,
  useUpdateCurrentUser,
} from "~/api/users/users";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { UpdateButton, UpdateButtonRef } from "~/components/UpdateButton";
import { ROUTES, useOrganizationParams } from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import { trackEvent } from "~/utils/analytics";
import { ThemeSelector } from "./ThemeSelector";

type FormData = {
  fullName: string;
};

export const SettingsAccount: FC = () => {
  useTrackPageVisit("app/settings_account/visit");

  const intl = useIntl();
  const navigate = useNavigate();
  const { mutate, isPending } = useUpdateCurrentUser();
  const { data: userInfo } = useGetCurrentUser();
  const updateButtonRef = useRef<UpdateButtonRef>(null);
  const { organizationId } = useOrganizationParams();

  const queryClient = useQueryClient();

  useEffect(() => {
    if (userInfo?.guestInOrganizations?.includes(organizationId)) {
      navigate(ROUTES.ACCESS_DENIED);
    }
  }, [userInfo, navigate, organizationId]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>();

  const onSubmit = (data: FormData) => {
    mutate(
      { data: { name: data.fullName } },
      {
        onSuccess: (data) => {
          updateButtonRef.current?.onSuccess();
          queryClient.setQueryData(
            getGetCurrentUserQueryKey(),
            (prev: User) => ({
              ...prev,
              name: data.name,
            })
          );
        },
      }
    );
  };

  useEffect(() => {
    if (userInfo?.name) {
      setValue("fullName", userInfo.name);
    }
  }, [userInfo?.name]);

  return (
    <div className="flex flex-col gap-6 mt-2 w-full max-w-[766px]">
      <h3 className="text-2xl font-semibold">
        <FormattedMessage id="settings.account" defaultMessage={"Account"} />
      </h3>
      <div className="flex gap-4 justify-between">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full flex flex-col gap-2"
        >
          <Label htmlFor="updateFullName">
            <FormattedMessage
              id="settings.fullName"
              defaultMessage="Full name"
            />
          </Label>
          <Input
            id="updateFullName"
            onClick={() => {
              trackEvent({
                eventName: "app/settings_account/name.input/click",
              });
            }}
            placeholder={intl.formatMessage({
              id: "settings.fullNamePlaceholder",
              defaultMessage: "Enter your name",
            })}
            {...register("fullName", {
              required: intl.formatMessage({
                id: "settings.errorFullName",
                defaultMessage: "Full name is required",
              }),
            })}
          />
          <span className="mb-4 text-sm text-muted-foreground">
            {errors.fullName ? (
              <span className="text-destructive">
                {errors.fullName.message}
              </span>
            ) : (
              <FormattedMessage
                id="settings.fullNameHint"
                defaultMessage={
                  "Your name may appear on Sky where you contribute or are mentioned, but can be removed anytime."
                }
              />
            )}
          </span>

          <Label htmlFor="userEmail" className="mt-4">
            <FormattedMessage id="settings.email" defaultMessage="Email" />
          </Label>
          <Input id="userEmail" disabled value={userInfo?.email} />
          <span className="mb-4 text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.emailHint"
              defaultMessage="To change your email, please contact support."
            />
          </span>

          <div className="border-t pt-4 mt-4">
            <ThemeSelector />
          </div>

          <UpdateButton isPending={isPending} ref={updateButtonRef} />
        </form>
      </div>
    </div>
  );
};
